import { FEATURE_FLAGS } from "@solomon-apps/config";
import type { ClientOperatingContextSummary } from "@solomon-apps/shared";
import { useOrgAuthStore } from "@solomon-apps/shared";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@solomon-apps/shared/src/components/ui/select";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { CLIENT_OPERATING_CONTEXTS_QUERY_KEY } from "@/hooks/useClientPlatformAccess";

import {
    clientOperatingContextStatusLabel,
    shouldShowClientOperatingContextSelector
} from "./clientOperatingContextSelectorModel";

export function ClientOperatingContextSelector(): React.ReactElement | null {
    const queryClient = useQueryClient();
    const selectorEnabled = FEATURE_FLAGS.clientOperatingContextSelection;
    const contexts = useOrgAuthStore((state) => state.availableClientContexts);
    const selectedClientContextUuid = useOrgAuthStore(
        (state) => state.selectedClientContextUuid
    );
    const setSelectedClientContextUuid = useOrgAuthStore(
        (state) => state.setSelectedClientContextUuid
    );

    if (
        !selectorEnabled ||
        !shouldShowClientOperatingContextSelector(contexts)
    ) {
        return null;
    }

    const selectedContext =
        contexts.find(
            (context) => context.issuerClientUuid === selectedClientContextUuid
        ) ?? null;

    return (
        <div className="px-2 py-2">
            <Text
                variant="label"
                render={<label htmlFor="client-context-selector" />}
                className="mb-1 block text-foreground-muted"
            >
                Distributor
            </Text>
            <Select
                value={selectedClientContextUuid ?? ""}
                onValueChange={(value) => {
                    const nextContextUuid =
                        typeof value === "string" && value ? value : null;
                    if (nextContextUuid === selectedClientContextUuid) return;
                    // Every client request is scoped by this selection. Drop
                    // cached responses before the next tree renders so one
                    // relationship's data cannot flash in another's context.
                    queryClient.removeQueries({
                        queryKey: ["yaas-client"]
                    });
                    setSelectedClientContextUuid(nextContextUuid);
                    void queryClient.invalidateQueries({
                        queryKey: [CLIENT_OPERATING_CONTEXTS_QUERY_KEY]
                    });
                }}
            >
                <SelectTrigger
                    id="client-context-selector"
                    aria-label="Distributor relationship"
                    size="sm"
                    surface="sunken"
                    className="w-full min-w-0"
                >
                    <SelectValue placeholder="Choose distributor">
                        {selectedContext ? (
                            <ContextSelectValue context={selectedContext} />
                        ) : null}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent align="start" alignItemWithTrigger={false}>
                    {contexts.map((context) => (
                        <SelectItem
                            key={context.issuerClientUuid}
                            value={context.issuerClientUuid}
                        >
                            <ContextSelectValue context={context} />
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function ContextSelectValue({
    context
}: {
    context: ClientOperatingContextSummary;
}): React.ReactElement {
    const statusLabel = clientOperatingContextStatusLabel(context);
    return (
        <span className="flex min-w-0 flex-col">
            <span className="truncate">{context.issuerOrganizationName}</span>
            {statusLabel ? (
                <span className="truncate text-xs leading-4 text-foreground-muted">
                    {statusLabel}
                </span>
            ) : null}
        </span>
    );
}
